import { useTranslation } from "react-i18next";
import PrimaryButton from "../../../../../../components/common/PrimaryButton";
import styles from "./styles.module.scss";
import { useContext, useMemo, useState, useEffect } from "react";
import { ContractContext } from "../../../../../../providers/contract";
import { ECONTRACT_STATUS } from "../../../../../../enums";
import classNames from "classnames";
import WaffyFeesSummary from "../components/WaffyFeesSummary";
import { AuthConfig } from "../../../../../../setup/context/config/authConfig";
import {
  ESenderRole,
  AddOnFeeCategory,
} from "../../../../../../setup/api/services/contract/models";
import { EContractActions } from "../../../../../../setup/api/services/contractActions/enum";
import { doAction } from "../../../../../../setup/api/services/contractActions";
import TextInput, {
  InputType,
} from "../../../../../../components/forms/textInput";
import { getShipmentFeesTotal } from "../../../../../../setup/util/contract";

interface DefaultSettlementProps {
  propose?: boolean;
  onSuccess?: () => void;
}

const DefaultSettlement = ({ propose, onSuccess }: DefaultSettlementProps) => {
  const { t } = useTranslation();
  const { contract, contractActivities, actions } = useContext(ContractContext);

  const wasItemAcceptedByBuyer = useMemo(
    () =>
      !!contractActivities?.find(
        (activity) => activity.to === ECONTRACT_STATUS.ITEM_ACCEPTED
      ),
    [contractActivities]
  );

  const [loading, setLoading] = useState(false);
  const [naqelFees, setNaqelFees] = useState<number | undefined>(undefined);

  const userIdentification = AuthConfig.getUserInfo().userIdentification;

  const shippingFeeAmount = useMemo(() => {
    if (!contract?.addonFeesList?.length) return 0;
    const shippingFee = contract.addonFeesList.find(
      (fee) => fee.type === AddOnFeeCategory.SHIPPING_FEES
    );
    return shippingFee?.amount || 0;
  }, [contract?.addonFeesList]);

  useEffect(() => {
    if (shippingFeeAmount > 0 && naqelFees === undefined) {
      setNaqelFees(shippingFeeAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingFeeAmount]);

  const waffyExtraFeesAmount = useMemo(() => {
    if (naqelFees === undefined || shippingFeeAmount === 0) return 0;
    const fees = naqelFees || 0;
    const remaining = shippingFeeAmount - fees;
    return remaining >= 0 ? remaining : 0;
  }, [naqelFees, shippingFeeAmount]);

  const userToSettle = useMemo(() => {
    if (
      !wasItemAcceptedByBuyer &&
      contract?.status === ECONTRACT_STATUS.READY_FOR_CASH_OUT
    ) {
      return "buyer";
    }

    if (
      contract?.status === ECONTRACT_STATUS.ITEM_ACCEPTED ||
      contract?.status === ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_CLOSE ||
      contract?.status === ECONTRACT_STATUS.READY_FOR_CASH_OUT
    ) {
      return "seller";
    }
    return "buyer";
  }, [contract, wasItemAcceptedByBuyer]);

  const totalAmountDue = useMemo(() => {
    return userToSettle === "seller" && contract?.originalItemPrice
      ? contract?.originalItemPrice
      : contract?.itemPrice ?? 0;
  }, [contract, userToSettle]);

  const userAmountDue = useMemo(() => {
    const isOldShipmentFee =
      !contract?.addonFeesList?.length && contract?.shipment?.fee;

    const oldExtraShipmentFee =
      (isOldShipmentFee ? contract?.shipment?.fee : 0) ?? 0;
    return totalAmountDue + oldExtraShipmentFee;
  }, [
    contract?.addonFeesList?.length,
    contract?.shipment?.fee,
    totalAmountDue,
  ]);

  const totalAmountDueWithFee = useMemo(() => {
    return totalAmountDue + getShipmentFeesTotal(contract);
  }, [contract, totalAmountDue]);

  const contractFeesAmount = useMemo(() => {
    const flatValue = contract?.feePercentage
      ? contract?.feePercentage * totalAmountDueWithFee
      : contract?.fee;
    const roundedTo2Decimals = flatValue
      ? Math.floor(flatValue * 100) / 100
      : 0;
    return roundedTo2Decimals;
  }, [contract?.fee, contract?.feePercentage, totalAmountDueWithFee]);

  const handleSettleContract = async () => {
    if (!contract) return;

    try {
      setLoading(true);

      if (propose) {
        const settleUserIdentification =
          userToSettle === "seller"
            ? String(
                contract?.partiesProfiles?.PROVIDER?.[0]?.userIdentification ??
                  contract?.parties?.find(
                    (p) => p.role === ESenderRole.PROVIDER
                  )?.userIdentification
              )
            : String(
                contract?.partiesProfiles?.CUSTOMER?.[0]?.userIdentification ??
                  contract?.parties?.find(
                    (p) => p.role === ESenderRole.CUSTOMER
                  )?.userIdentification
              );

        const resolution =
          userToSettle === "seller" ? "RESOLVED_PROVIDER" : "RESOLVED_CUSTOMER";

        const brokerUserIdentification = contract?.parties?.find(
          (p) => p.role == ESenderRole.BROKER
        )?.userId;

        await doAction({
          contractId: contract.id,
          userId: String(userIdentification),
          actorRole: ESenderRole.CLIENT_ADMIN,
          actorOrgCode: contract.organizationCode,
          contractAction: EContractActions.CLIENT_RESOLVE_ESCALATION,
          escalationDTO: {
            resolution,
            proposedSettlement: [
              {
                id: settleUserIdentification,
                amountDue: userAmountDue - (contract?.fee ?? 0),
              },
              brokerUserIdentification && {
                id: String(brokerUserIdentification),
                amountDue: contract?.fee ?? 0,
              },
            ],
          },
        } as any);
        actions.getContract();
        onSuccess?.();
      } else {
        await doAction({
          contractId: contract?.id,
          userId: String(userIdentification),
          actorRole: ESenderRole.CLIENT_ADMIN,
          actorOrgCode: contract.organizationCode,
          contractAction: EContractActions.CLIENT_ACCEPT_SETTLEMENT,
        });
        window.location.reload();
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <WaffyFeesSummary
          feeAmount={contractFeesAmount ?? 0}
          remainingShippingFeeAmount={waffyExtraFeesAmount}
        />

        <h2 className={classNames(styles["subtitle"], styles["h2"])}>
          {userToSettle === "seller" ? t("lbl_the_seller") : t("lbl_the_buyer")}
        </h2>
        <div className={styles.table_container}>
          <div className={styles.table_item}>
            <div className={styles.table_item_content}>
              <p className={classNames(styles["g600"], styles["subtitle2"])}>
                {t("lbl_settlement_total_amount")}
              </p>

              <h2 className={classNames(styles["g700"], styles["h2"])}>
                {`${totalAmountDueWithFee} ${t("lbl_saudi_currency")}`}
              </h2>
            </div>
          </div>
          <div className={styles.vertical_line}></div>
          <div className={styles.table_item}>
            <div className={styles.table_item_content}>
              <p className={classNames(styles["g600"], styles["subtitle2"])}>
                {t("lbl_more_details_sheet_fees")}
              </p>
              <h2 className={classNames(styles["g700"], styles["h2"])}>
                {`${(contract?.feePercentage || 0) * 100}% - ${
                  contractFeesAmount ?? 0
                }${t("lbl_saudi_currency")}
                `}
              </h2>
            </div>
          </div>
          <div className={styles.vertical_line}></div>
          <div className={styles.table_item}>
            <div className={styles.table_item_content}>
              <p className={classNames(styles["g600"], styles["subtitle2"])}>
                {t("lbl_deserved_amount")}
              </p>

              <h2 className={classNames(styles["g700"], styles["h2"])}>
                {`${userAmountDue} ${t("lbl_saudi_currency")}`}
              </h2>
            </div>
          </div>
          <div className={styles.vertical_line}></div>
          <div className={styles.table_item}>
            <div className={styles.table_item_content}>
              <p className={classNames(styles["g600"], styles["subtitle2"])}>
                {t("lbl_loyal_receive")}
              </p>
              <h2 className={classNames(styles["doneText"], styles["h2"])}>
                {(userAmountDue - (contractFeesAmount ?? 0)).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  }
                ) +
                  " " +
                  t("lbl_saudi_currency")}
              </h2>
            </div>
          </div>
        </div>
        {contract?.shippable && getShipmentFeesTotal(contract) > 0 && (
          <div className={styles.sub_section}>
            <h2 className={classNames(styles["subtitle"], styles["h2"])}>
              {t("lbl_naqel")}
            </h2>
            <div className={styles.total_amount_container}>
              <TextInput
                label={t("lbl_amount_received")}
                maxValue={shippingFeeAmount}
                type={InputType.NUMBER}
                handleChange={(e) => {
                  setNaqelFees(Number(e.target.value));
                }}
                value={naqelFees}
                description={t("lbl_saudi_currency")}
                preventMaxValuePass
                outerContainerWidth="100%"
                containerWidth="100%"
                placeholder="0.0"
              />
            </div>
          </div>
        )}
      </div>
      <div className={styles.cta_container}>
        <PrimaryButton
          style={{
            backgroundColor: loading ? "#9FA6AB" : "#0D44AD",
            height: "48px",
            color: "#FFFFFF",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: "1rem",
            fontWeight: 600,
          }}
          onClick={handleSettleContract}
          disabled={loading}>
          {propose ? t("lbl_propose_settlement") : t("lbl_confirm_settlement")}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default DefaultSettlement;
